/**
 * Bot Config Service
 * Generic CRUD for custom LLM bot configurations
 */

import { createBotConfig } from './bot-config.js'

/**
 * Create bot config service
 * @param {Object} storageAdapter - Storage adapter with get/set methods
 * @param {Object} options
 * @param {string} options.storageKey - Storage key for persisting bots
 * @param {Object[]} [options.builtInBots] - Built-in bot configs (immutable, prepended to list)
 * @param {string[]} [options.defaultToolCategories] - Default tool categories for new bots
 */
export function createBotConfigService(storageAdapter, { storageKey, builtInBots = [], defaultToolCategories = [] }) {
  const builtInIds = new Set(builtInBots.map(b => b.botId))

  const loadBots = async () => {
    const stored = await storageAdapter.get(storageKey)
    if (!stored) return []
    try {
      return JSON.parse(stored)
    } catch {
      return []
    }
  }

  const saveBots = async (bots) => {
    await storageAdapter.set(storageKey, JSON.stringify(bots))
  }

  const getBots = async () => {
    const custom = await loadBots()
    return [...builtInBots, ...custom]
  }

  const getBot = async (botId) => {
    const builtIn = builtInBots.find(b => b.botId === botId)
    if (builtIn) return builtIn
    const bots = await loadBots()
    return bots.find(b => b.botId === botId) || null
  }

  const createBot = async (config) => {
    const bot = createBotConfig(config, defaultToolCategories)
    const bots = await loadBots()
    bots.push(bot)
    await saveBots(bots)
    return bot
  }

  const updateBot = async (botId, updates) => {
    if (builtInIds.has(botId)) throw new Error('Cannot modify built-in bot')
    const bots = await loadBots()
    const idx = bots.findIndex(b => b.botId === botId)
    if (idx < 0) throw new Error('Bot not found')
    bots[idx] = { ...bots[idx], ...updates, botId }
    await saveBots(bots)
    return bots[idx]
  }

  const deleteBot = async (botId) => {
    if (builtInIds.has(botId)) throw new Error('Cannot delete built-in bot')
    const bots = await loadBots()
    await saveBots(bots.filter(b => b.botId !== botId))
  }

  return { getBots, getBot, createBot, updateBot, deleteBot }
}
